import ChartSkeleton from '@/components/skeletons/ChartSkeleton'
import { getServiceAnalyticUsages } from '@/modules/Service/services'
import {
    MonitoringQueries,
    ServiceMonitoringProps,
} from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import React, { useState } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useTranslation } from 'react-i18next'
import Header from '../../../shared/Header'
import AnalyticSeriesChart, {
    AnalyticSeriesData,
} from '../../../shared/AnalyticSeriesChart'

const today = dayjs()

// Recording (LMS) transcoding duration (hours). Single-metric time series from
// the margarita-backed endpoint (type=/lms_recording_durations).
const Monitoring: React.FC<ServiceMonitoringProps> = ({ service }) => {
    const { t } = useTranslation()
    const [analyticQueries, setAnalyticQueries] = useState<MonitoringQueries>({
        range: 'last-month',
        gte: today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        lte: today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    })

    const { data, renderState, isPending } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES,
            'analytics',
            service?.service_creatable_uuid,
            'recording',
            analyticQueries?.gte,
            analyticQueries?.lte,
        ],
        queryFn: () =>
            getServiceAnalyticUsages({
                serviceID: service?.service_id,
                params: {
                    gte: formatDateWithFormatter(
                        analyticQueries?.gte,
                        'YYYY-MM-DD'
                    ),
                    lte: formatDateWithFormatter(
                        analyticQueries?.lte,
                        'YYYY-MM-DD'
                    ),
                    type: '/lms_recording_durations',
                },
            }),
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    if (isPending) return <ChartSkeleton />

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-2"
        >
            <Header
                analyticQueries={analyticQueries}
                setAnalyticQueries={setAnalyticQueries}
            />

            <AnalyticSeriesChart
                data={data as AnalyticSeriesData}
                title={t('services.recording-analytics')}
                unit="hours"
                color="#8b5cf6"
                variant="bar"
                totalLabel="Total Hours"
                emptyText="No recording data available for the selected period"
            />
        </motion.div>
    )
}

export default Monitoring
